import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';
import { Pipeline, PipelineNode, PipelineConnection, PipelineNodeType, PipelineNodeConfig } from '../types/pipeline';
import { v4 as uuidv4 } from 'uuid';

interface PipelineBuilderContextType {
  pipeline: Pipeline | null;
  selectedNodeId: string | null;
  nodes: PipelineNode[];
  connections: PipelineConnection[];
  history: Pipeline[];
  historyIndex: number;

  // Actions
  createNewPipeline: (name: string, description?: string) => void;
  loadPipeline: (pipeline: Pipeline) => void;
  addNode: (node: PipelineNode) => void;
  removeNode: (nodeId: string) => void;
  updateNode: (nodeId: string, updates: Partial<PipelineNode>) => void;
  selectNode: (nodeId: string | null) => void;
  addConnection: (connection: PipelineConnection) => void;
  removeConnection: (connectionId: string) => void;
  disconnectNode: (fromNodeId: string, toNodeId: string) => void;
  undo: () => void;
  redo: () => void;
  reset: () => void;
  getPipelineJSON: () => string;
}

const PipelineBuilderContext = createContext<PipelineBuilderContextType | undefined>(undefined);

type Action =
  | { type: 'CREATE_NEW_PIPELINE'; name: string; description?: string }
  | { type: 'LOAD_PIPELINE'; pipeline: Pipeline }
  | { type: 'ADD_NODE'; node: PipelineNode }
  | { type: 'REMOVE_NODE'; nodeId: string }
  | { type: 'UPDATE_NODE'; nodeId: string; updates: Partial<PipelineNode> }
  | { type: 'SELECT_NODE'; nodeId: string | null }
  | { type: 'ADD_CONNECTION'; connection: PipelineConnection }
  | { type: 'REMOVE_CONNECTION'; connectionId: string }
  | { type: 'DISCONNECT_NODE'; fromNodeId: string; toNodeId: string }
  | { type: 'UNDO' }
  | { type: 'REDO' }
  | { type: 'RESET' };

interface ReducerState {
  pipeline: Pipeline | null;
  selectedNodeId: string | null;
  nodes: PipelineNode[];
  connections: PipelineConnection[];
  history: Pipeline[];
  historyIndex: number;
}

const initialState: ReducerState = {
  pipeline: null,
  selectedNodeId: null,
  nodes: [],
  connections: [],
  history: [],
  historyIndex: -1,
};

const reducer = (state: ReducerState, action: Action): ReducerState => {
  switch (action.type) {
    case 'CREATE_NEW_PIPELINE': {
      const newPipeline: Pipeline = {
        id: uuidv4(),
        name: action.name,
        description: action.description,
        version: '1.0',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        nodes: [],
        connections: [],
        variables: {},
        metadata: {},
      };
      return {
        ...state,
        pipeline: newPipeline,
        nodes: [],
        connections: [],
        history: [newPipeline],
        historyIndex: 0,
      };
    }

    case 'LOAD_PIPELINE': {
      return {
        ...state,
        pipeline: action.pipeline,
        nodes: action.pipeline.nodes,
        connections: action.pipeline.connections,
        history: [action.pipeline],
        historyIndex: 0,
      };
    }

    case 'ADD_NODE': {
      if (!state.pipeline) return state;

      const newNodes = [...state.nodes, action.node];
      const newPipeline: Pipeline = {
        ...state.pipeline,
        nodes: newNodes,
        updatedAt: new Date().toISOString(),
      };

      return {
        ...state,
        nodes: newNodes,
        pipeline: newPipeline,
        history: [...state.history.slice(0, state.historyIndex + 1), newPipeline],
        historyIndex: state.historyIndex + 1,
      };
    }

    case 'REMOVE_NODE': {
      if (!state.pipeline) return state;

      const newNodes = state.nodes.filter(n => n.id !== action.nodeId);
      const newConnections = state.connections.filter(
        c => c.fromNodeId !== action.nodeId && c.toNodeId !== action.nodeId
      );

      const newPipeline: Pipeline = {
        ...state.pipeline,
        nodes: newNodes,
        connections: newConnections,
        updatedAt: new Date().toISOString(),
      };

      return {
        ...state,
        nodes: newNodes,
        connections: newConnections,
        pipeline: newPipeline,
        selectedNodeId: state.selectedNodeId === action.nodeId ? null : state.selectedNodeId,
        history: [...state.history.slice(0, state.historyIndex + 1), newPipeline],
        historyIndex: state.historyIndex + 1,
      };
    }

    case 'UPDATE_NODE': {
      if (!state.pipeline) return state;

      const newNodes = state.nodes.map(node =>
        node.id === action.nodeId ? { ...node, ...action.updates } : node
      );

      const newPipeline: Pipeline = {
        ...state.pipeline,
        nodes: newNodes,
        updatedAt: new Date().toISOString(),
      };

      return {
        ...state,
        nodes: newNodes,
        pipeline: newPipeline,
        history: [...state.history.slice(0, state.historyIndex + 1), newPipeline],
        historyIndex: state.historyIndex + 1,
      };
    }

    case 'SELECT_NODE': {
      return {
        ...state,
        selectedNodeId: action.nodeId,
      };
    }

    case 'ADD_CONNECTION': {
      if (!state.pipeline) return state;

      const newConnections = [...state.connections, action.connection];
      const newPipeline: Pipeline = {
        ...state.pipeline,
        connections: newConnections,
        updatedAt: new Date().toISOString(),
      };

      return {
        ...state,
        connections: newConnections,
        pipeline: newPipeline,
        history: [...state.history.slice(0, state.historyIndex + 1), newPipeline],
        historyIndex: state.historyIndex + 1,
      };
    }

    case 'REMOVE_CONNECTION': {
      if (!state.pipeline) return state;

      const newConnections = state.connections.filter(c => c.id !== action.connectionId);

      const newPipeline: Pipeline = {
        ...state.pipeline,
        connections: newConnections,
        updatedAt: new Date().toISOString(),
      };

      return {
        ...state,
        connections: newConnections,
        pipeline: newPipeline,
        history: [...state.history.slice(0, state.historyIndex + 1), newPipeline],
        historyIndex: state.historyIndex + 1,
      };
    }

    case 'DISCONNECT_NODE': {
      if (!state.pipeline) return state;

      const newConnections = state.connections.filter(
        c => !(c.fromNodeId === action.fromNodeId && c.toNodeId === action.toNodeId)
      );

      const newPipeline: Pipeline = {
        ...state.pipeline,
        connections: newConnections,
        updatedAt: new Date().toISOString(),
      };

      return {
        ...state,
        connections: newConnections,
        pipeline: newPipeline,
        history: [...state.history.slice(0, state.historyIndex + 1), newPipeline],
        historyIndex: state.historyIndex + 1,
      };
    }

    case 'UNDO': {
      if (state.historyIndex <= 0) return state;
      const newIndex = state.historyIndex - 1;
      const pipeline = state.history[newIndex];
      return {
        ...state,
        pipeline,
        nodes: pipeline.nodes,
        connections: pipeline.connections,
        historyIndex: newIndex,
      };
    }

    case 'REDO': {
      if (state.historyIndex >= state.history.length - 1) return state;
      const newIndex = state.historyIndex + 1;
      const pipeline = state.history[newIndex];
      return {
        ...state,
        pipeline,
        nodes: pipeline.nodes,
        connections: pipeline.connections,
        historyIndex: newIndex,
      };
    }

    case 'RESET': {
      return initialState;
    }

    default:
      return state;
  }
};

export const PipelineBuilderProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const createNewPipeline = useCallback((name: string, description?: string) => {
    dispatch({ type: 'CREATE_NEW_PIPELINE', name, description });
  }, []);

  const loadPipeline = useCallback((pipeline: Pipeline) => {
    dispatch({ type: 'LOAD_PIPELINE', pipeline });
  }, []);

  const addNode = useCallback((node: PipelineNode) => {
    dispatch({ type: 'ADD_NODE', node });
  }, []);

  const removeNode = useCallback((nodeId: string) => {
    dispatch({ type: 'REMOVE_NODE', nodeId });
  }, []);

  const updateNode = useCallback((nodeId: string, updates: Partial<PipelineNode>) => {
    dispatch({ type: 'UPDATE_NODE', nodeId, updates });
  }, []);

  const selectNode = useCallback((nodeId: string | null) => {
    dispatch({ type: 'SELECT_NODE', nodeId });
  }, []);

  const addConnection = useCallback((connection: PipelineConnection) => {
    dispatch({ type: 'ADD_CONNECTION', connection });
  }, []);

  const removeConnection = useCallback((connectionId: string) => {
    dispatch({ type: 'REMOVE_CONNECTION', connectionId });
  }, []);

  const disconnectNode = useCallback((fromNodeId: string, toNodeId: string) => {
    dispatch({ type: 'DISCONNECT_NODE', fromNodeId, toNodeId });
  }, []);

  const undo = useCallback(() => {
    dispatch({ type: 'UNDO' });
  }, []);

  const redo = useCallback(() => {
    dispatch({ type: 'REDO' });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  const getPipelineJSON = useCallback(() => {
    return JSON.stringify(state.pipeline, null, 2);
  }, [state.pipeline]);

  const value: PipelineBuilderContextType = {
    pipeline: state.pipeline,
    selectedNodeId: state.selectedNodeId,
    nodes: state.nodes,
    connections: state.connections,
    history: state.history,
    historyIndex: state.historyIndex,
    createNewPipeline,
    loadPipeline,
    addNode,
    removeNode,
    updateNode,
    selectNode,
    addConnection,
    removeConnection,
    disconnectNode,
    undo,
    redo,
    reset,
    getPipelineJSON,
  };

  return (
    <PipelineBuilderContext.Provider value={value}>
      {children}
    </PipelineBuilderContext.Provider>
  );
};

export const usePipelineBuilder = () => {
  const context = useContext(PipelineBuilderContext);
  if (context === undefined) {
    throw new Error('usePipelineBuilder must be used within PipelineBuilderProvider');
  }
  return context;
};
